import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Plus, Copy, Trash2 } from 'lucide-react';
import ReceiptUploader from './ReceiptUploader';

/**
 * Single expense row in batch dialog
 */
const BatchExpenseRow = ({ 
  expense, 
  index, 
  projects, 
  expenseCategories, 
  currencies,
  getCategoryDisplayName,
  onUpdate, 
  onRemove, 
  onDuplicate,
  canRemove 
}) => {
  const { t } = useTranslation();
  const getSubcategories = () => {
    const cat = expenseCategories[expense.category];
    return Array.isArray(cat) ? cat : (cat?.subcategories || []);
  };

  return (
    <Card className="p-4">
      <div className="flex justify-between items-start mb-3">
        <span className="text-sm font-medium text-gray-500">{t('expenses.batch.rowTitle', { n: index + 1 })}</span>
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onDuplicate}
            title={t('expenses.batch.duplicate')}
           data-testid="btn-duplicate">
            <Copy className="h-4 w-4" />
          </Button>
          {canRemove && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onRemove}
              title={t('expenses.batch.remove')}
             data-testid="btn-remove">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
        <div>
          <Label className="text-xs">{t('expenses.fields.project')}</Label>
          <Select 
            value={expense.project_id} 
            onValueChange={(v) => onUpdate('project_id', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={t('expenses.ph.select')} />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">{t('expenses.fields.category')}</Label>
          <Select 
            value={expense.category} 
            onValueChange={(v) => onUpdate('category', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(expenseCategories)
                .filter(([, cat]) => Array.isArray(cat) || cat.visible !== false)
                .map(([key]) => (
                  <SelectItem key={key} value={key}>{getCategoryDisplayName(key)}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">{t('expenses.fields.subcategory')}</Label>
          <Select 
            value={expense.subcategory} 
            onValueChange={(v) => onUpdate('subcategory', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={t('expenses.ph.select')} />
            </SelectTrigger>
            <SelectContent>
              {getSubcategories().map(sub => (
                <SelectItem key={sub} value={sub}>{sub}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">{t('expenses.fields.amount')}</Label>
          <Input
            type="number"
            step="0.01"
            value={expense.amount}
            onChange={(e) => onUpdate('amount', e.target.value)}
            placeholder={t('expenses.ph.amount')}
            className="h-9"
          />
        </div>
        
        <div>
          <Label className="text-xs">{t('expenses.fields.currency')}</Label>
          <Select 
            value={expense.currency} 
            onValueChange={(v) => onUpdate('currency', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="lg:col-span-2">
          <Label className="text-xs">{t('expenses.fields.date')}</Label>
          <Input
            type="date"
            value={expense.expense_date}
            onChange={(e) => onUpdate('expense_date', e.target.value)}
            className="h-9"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div>
          <Label className="text-xs">{t('expenses.fields.description')}</Label>
          <Input
            value={expense.description}
            onChange={(e) => onUpdate('description', e.target.value)}
            placeholder={t('expenses.ph.descriptionOptional')}
            className="h-9"
          />
        </div>
        <div>
          <Label className="text-xs">{t('expenses.fields.vendor')}</Label>
          <Input
            value={expense.vendor}
            onChange={(e) => onUpdate('vendor', e.target.value)}
            placeholder={t('expenses.ph.vendorOptional')}
            className="h-9"
          />
        </div>
      </div>
      
      {/* Receipt Upload */}
      <div className="mt-3">
        <Label className="text-xs">{t('expenses.fields.receiptsMax')}</Label>
        <ReceiptUploader
          receipts={[]}
          pendingFiles={expense.receipts || []}
          onFilesChange={(files) => onUpdate('receipts', files)}
          maxReceipts={5}
          compact={true}
        />
      </div>
    </Card>
  );
};

/**
 * Batch Expense Entry Dialog Component
 */
const BatchExpenseDialog = ({
  open,
  onOpenChange,
  projects,
  expenseCategories,
  currencies,
  getCategoryDisplayName,
  onSubmit
}) => {
  const { t } = useTranslation();
  // Use a ref-based counter for stable ID generation
  const idCounterRef = useRef(0);
  
  const generateId = useCallback(() => {
    idCounterRef.current += 1;
    return `batch-expense-${idCounterRef.current}`;
  }, []);

  const createEmptyBatchExpense = useCallback(() => ({
    id: generateId(),
    project_id: '',
    expense_type: 'project_cost',
    category: 'travel',
    subcategory: '',
    amount: '',
    currency: 'EUR',
    expense_date: new Date().toISOString().split('T')[0],
    description: '',
    vendor: '',
    receipts: []
  }), [generateId]);

  const [batchExpenses, setBatchExpenses] = useState([]);
  const [submitImmediately, setSubmitImmediately] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize with one expense when dialog opens
  React.useEffect(() => {
    if (open && !initialized) {
      setBatchExpenses([createEmptyBatchExpense()]);
      setInitialized(true);
    }
    if (!open) {
      setInitialized(false);
    }
  }, [open, initialized, createEmptyBatchExpense]);

  const handleOpen = (isOpen) => {
    if (isOpen) {
      setBatchExpenses([createEmptyBatchExpense()]);
      setSubmitImmediately(false);
    }
    onOpenChange(isOpen);
  };

  const updateBatchExpense = (index, field, value) => {
    const updated = [...batchExpenses];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'category') {
      updated[index].subcategory = '';
    }
    setBatchExpenses(updated);
  };

  const addBatchExpense = () => {
    setBatchExpenses([...batchExpenses, createEmptyBatchExpense()]);
  };

  const removeBatchExpense = (index) => {
    setBatchExpenses(batchExpenses.filter((_, i) => i !== index));
  };

  const duplicateBatchExpense = (index) => {
    const expense = { ...batchExpenses[index], id: generateId(), receipts: [] };
    const updated = [...batchExpenses];
    updated.splice(index + 1, 0, expense);
    setBatchExpenses(updated);
  };

  const handleSubmit = async () => {
    // Validate
    const invalidExpenses = batchExpenses.filter(exp => 
      !exp.project_id || !exp.amount || !exp.subcategory
    );
    
    if (invalidExpenses.length > 0) {
      return; // Let parent handle validation toast
    }

    if (batchExpenses.length === 0) {
      return;
    }

    setIsSubmitting(true);
    const result = await onSubmit(batchExpenses, submitImmediately);
    setIsSubmitting(false);
    
    if (result?.success) {
      handleOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('expenses.dialog.batch')}</DialogTitle>
          <DialogDescription>
            {t('expenses.dialog.batchDesc')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {batchExpenses.map((expense, index) => (
            <BatchExpenseRow
              key={expense.id}
              expense={expense}
              index={index}
              projects={projects}
              expenseCategories={expenseCategories}
              currencies={currencies}
              getCategoryDisplayName={getCategoryDisplayName}
              onUpdate={(field, value) => updateBatchExpense(index, field, value)}
              onRemove={() => removeBatchExpense(index)}
              onDuplicate={() => duplicateBatchExpense(index)}
              canRemove={batchExpenses.length > 1}
            />
          ))}
          
          <Button variant="outline" onClick={addBatchExpense} className="w-full" data-testid="btn-addbatchexpense">
            <Plus className="h-4 w-4 mr-2" /> {t('expenses.actions.addAnother')}
          </Button>
          
          <div className="flex items-center space-x-2 pt-4 border-t">
            <Checkbox 
              id="submit-immediately"
              checked={submitImmediately}
              onCheckedChange={setSubmitImmediately}
            />
            <Label htmlFor="submit-immediately" className="text-sm">
              {t('expenses.actions.submitNow')}
            </Label>
          </div>
        </div>
        
        <DialogFooter>
          <div className="flex justify-between w-full items-center">
            <span className="text-sm text-gray-500">
              {t('expenses.batch.toCreate', { count: batchExpenses.length })}
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => handleOpen(false)}
                disabled={isSubmitting}
               data-testid="dialog-cancel-btn">
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
               data-testid="dialog-submit-btn">
                {isSubmitting ? t('expenses.batch.creating') : t('expenses.batch.createCount', { count: batchExpenses.length })}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchExpenseDialog;
